import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  descriptorToExportParams,
  EMPTY_MATRIX_FILTERS,
  matrixFilterSummary,
  type TrainingMatrixDescriptor,
  type TrainingMatrixView,
} from '@cls/shared';

type View = 'current' | 'deployed-by-project' | 'deployed-flat' | 'visa-expiry';
type Format = 'xlsx' | 'pdf';

interface Props {
  onClose: () => void;
  /**
   * The live view/filter/sort state of the Training page. The export endpoints
   * re-run the same shared matrix builder server-side, so whichever option is
   * picked, the file reproduces exactly what the screen would show for it.
   */
  descriptor: TrainingMatrixDescriptor;
  visibleCount?: number;
  totalWorkers?: number;
}

const VIEW_OPTIONS: { v: View; title: string; sub: string }[] = [
  { v: 'current',             title: 'Current view',                  sub: "what you're looking at, with all your on-screen filters applied" },
  { v: 'deployed-by-project', title: 'Deployed — by project',         sub: 'deployed workers grouped under their live projects' },
  { v: 'deployed-flat',       title: 'Deployed — flat',               sub: 'deployed workers as a single flat list' },
  { v: 'visa-expiry',         title: 'Visa expiry report',            sub: 'all visa-holders sorted by expiry date, with phone + email' },
];

export function TrainExportModal({ onClose, descriptor, visibleCount, totalWorkers }: Props) {
  const [view, setView] = useState<View>('current');
  const [format, setFormat] = useState<Format>('xlsx');
  const [ignoreFilters, setIgnoreFilters] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasFilters = matrixFilterSummary(descriptor.filters) !== 'None';
  const isDeployedOption = view === 'deployed-by-project' || view === 'deployed-flat';

  const run = () => {
    let url: string;
    if (view === 'visa-expiry') {
      url = `/api/reports/visa-expiry.${format}`;
    } else {
      let d: TrainingMatrixDescriptor = view === 'current'
        ? descriptor
        : { ...descriptor, view: view as TrainingMatrixView };
      if (isDeployedOption && ignoreFilters) d = { ...d, filters: { ...EMPTY_MATRIX_FILTERS } };
      const params = new URLSearchParams(descriptorToExportParams(d));
      url = `/api/reports/training/matrix.${format}?${params.toString()}`;
    }
    window.open(url, '_blank');
    onClose();
  };

  const currentCount = visibleCount ?? totalWorkers ?? 0;
  const total = totalWorkers ?? 0;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">⬇ Export Training</div>
        <div className="modal-sub" style={{ marginBottom: 14 }}>Choose which view to export and the file format.</div>
        <div className="form-row">
          <label className="form-label">View</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {VIEW_OPTIONS.map((o) => {
              const isCurrent = o.v === 'current';
              const suffix = isCurrent
                ? (total > 0 ? ` · ${currentCount} of ${total} workers` : '')
                : '';
              return (
                <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px' }}>
                  <input type="radio" name="te-view" value={o.v} checked={view === o.v} onChange={() => setView(o.v)} style={{ accentColor: 'var(--accent)' }} />
                  <span><strong>{o.title}</strong> — {o.sub}{suffix}</span>
                </label>
              );
            })}
          </div>
        </div>
        {isDeployedOption && hasFilters ? (
          <div className="form-row">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={ignoreFilters} onChange={(e) => setIgnoreFilters(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
              <span>Ignore my filters — export the full deployed snapshot ({matrixFilterSummary(descriptor.filters)})</span>
            </label>
          </div>
        ) : null}
        <div className="form-row">
          <label className="form-label">Format</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px' }}>
              <input type="radio" name="te-fmt" value="xlsx" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} style={{ accentColor: 'var(--accent)' }} /> <span>📊 Excel (.xlsx)</span>
            </label>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px' }}>
              <input type="radio" name="te-fmt" value="pdf" checked={format === 'pdf'} onChange={() => setFormat('pdf')} style={{ accentColor: 'var(--accent)' }} /> <span>📄 PDF</span>
            </label>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={run}>Export</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
